/**
 * 資料管理 一覧画面 (Phase 8 / v3.2.28+)。
 * タブで「公開閲覧 (viewer)」と「PDF ダウンロード」を切り替える。
 */
import type { DocumentStatus } from '@goenhub/shared';
import { BookOpen, FileDown, Lock, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  type DocumentRecord,
  useDeleteDocument,
  useDocuments,
} from '@/hooks/useDocuments';
import { paths } from '@/routes/paths';

import { DocumentCreateDialog } from './components/DocumentCreateDialog';
import { PdfDocumentCreateDialog } from './components/PdfDocumentCreateDialog';

const STATUS_LABEL: Record<DocumentStatus, string> = {
  draft: '下書き',
  published: '公開中',
  archived: 'アーカイブ',
};

const STATUS_BADGE_VARIANT: Record<
  DocumentStatus,
  'accent' | 'draft' | 'default'
> = {
  draft: 'draft',
  published: 'accent',
  archived: 'default',
};

function formatDate(d: unknown): string {
  if (!d) return '';
  if (typeof d === 'object' && d !== null && 'toDate' in d) {
    try {
      const date = (d as { toDate(): Date }).toDate();
      return date.toLocaleDateString('ja-JP');
    } catch {
      return '';
    }
  }
  return '';
}

type TabValue = 'viewer' | 'pdf';

export function DocumentsListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useDocuments();
  const deleteMutation = useDeleteDocument();
  const [tab, setTab] = useState<TabValue>('viewer');
  const [createOpen, setCreateOpen] = useState(false);
  const [pdfCreateOpen, setPdfCreateOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /**
   * 削除ボタンのクリック処理。
   * カード全体のクリック (編集画面遷移) と衝突しないよう stopPropagation。
   * PDF 型ならファイル + 発行済みトークンもカスケード削除（deleteDocument Function 側で処理）。
   */
  const handleDelete = async (
    e: React.MouseEvent,
    d: DocumentRecord,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const isPdf = d.type === 'pdf_download';
    const detail = isPdf
      ? '\n\n・PDF ファイル (Storage)\n・発行済みのダウンロードリンク (全て)\nも同時に削除されます。'
      : '';
    if (
      !confirm(
        `資料「${d.title}」を完全に削除します。この操作は取り消せません。${detail}\n\n続行しますか？`,
      )
    )
      return;
    setDeletingId(d.id);
    try {
      await deleteMutation.mutateAsync(d.id);
      toast.success(`「${d.title}」を削除しました`);
    } catch (err) {
      toast.error(
        `削除失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setDeletingId(null);
    }
  };

  const viewerDocs = useMemo(
    () => (data ?? []).filter((d) => (d.type ?? 'viewer') === 'viewer'),
    [data],
  );
  const pdfDocs = useMemo(
    () => (data ?? []).filter((d) => d.type === 'pdf_download'),
    [data],
  );

  const handleViewerCreated = (id: string) => {
    navigate(paths.documentEdit.replace(':id', id));
  };
  const handlePdfCreated = (id: string) => {
    navigate(paths.pdfDocumentViewer.replace(':id', id));
  };

  const emptyLabel =
    tab === 'viewer' ? 'まだ公開閲覧資料がありません。' : 'まだ PDF 資料がありません。';

  return (
    <Screen
      title="資料管理"
      description="資料請求フォームから自動配信される公開資料の管理。公開閲覧型 (13 種モジュール) と PDF ダウンロード型の 2 タイプ。"
      actions={
        <Button
          type="button"
          variant="primary"
          onClick={() => (tab === 'viewer' ? setCreateOpen(true) : setPdfCreateOpen(true))}
        >
          <Plus className="size-4 mr-1" />
          新規作成
        </Button>
      }
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
        <TabsList>
          <TabsTrigger value="viewer">
            <BookOpen className="size-4 mr-1.5" />
            公開閲覧資料
            <span className="ml-1.5 text-[11px] text-[var(--text-muted)]">
              {viewerDocs.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="pdf">
            <FileDown className="size-4 mr-1.5" />
            PDF ダウンロード
            <span className="ml-1.5 text-[11px] text-[var(--text-muted)]">
              {pdfDocs.length}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="viewer">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : viewerDocs.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              message={emptyLabel}
              onCreate={() => setCreateOpen(true)}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {viewerDocs.map((d) => (
                <DocumentCard
                  key={d.id}
                  onOpen={() =>
                    navigate(paths.documentEdit.replace(':id', d.id))
                  }
                  onDelete={(e) => void handleDelete(e, d)}
                  deleting={deletingId === d.id}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-sm truncate">{d.title}</h3>
                    <Badge variant={STATUS_BADGE_VARIANT[d.status]}>
                      {STATUS_LABEL[d.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mb-2 flex items-center gap-2">
                    <span className="font-mono">/{d.slug || '(slug 未設定)'}</span>
                    {d.passwordHash && (
                      <span className="inline-flex items-center gap-0.5">
                        <Lock className="size-3" />
                        保護
                      </span>
                    )}
                  </p>
                  <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                    <span>{d.sections?.length ?? 0} セクション</span>
                    <span>更新 {formatDate(d.updatedAt)}</span>
                    {d.status === 'published' && d.slug && (
                      <span className="text-[var(--accent)]">/docs/{d.slug}</span>
                    )}
                  </div>
                </DocumentCard>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pdf">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : pdfDocs.length === 0 ? (
            <EmptyState
              icon={FileDown}
              message={emptyLabel}
              onCreate={() => setPdfCreateOpen(true)}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {pdfDocs.map((d) => (
                <DocumentCard
                  key={d.id}
                  onOpen={() =>
                    navigate(paths.pdfDocumentViewer.replace(':id', d.id))
                  }
                  onDelete={(e) => void handleDelete(e, d)}
                  deleting={deletingId === d.id}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-sm truncate">{d.title}</h3>
                    <Badge variant={STATUS_BADGE_VARIANT[d.status]}>
                      {STATUS_LABEL[d.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mb-2 flex items-center gap-2">
                    <span className="font-mono">/dl/{d.slug || '(slug 未設定)'}</span>
                  </p>
                  <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                    {d.pdfFile ? (
                      <>
                        <span>
                          {d.pdfFile.fileName}
                          {d.pdfFile.pageCount
                            ? ` (${d.pdfFile.pageCount}ページ)`
                            : ''}
                        </span>
                        <span>
                          {(d.pdfFile.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </>
                    ) : (
                      <span className="text-amber-600">PDF 未アップロード</span>
                    )}
                    <span>更新 {formatDate(d.updatedAt)}</span>
                  </div>
                </DocumentCard>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <DocumentCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleViewerCreated}
      />
      <PdfDocumentCreateDialog
        open={pdfCreateOpen}
        onOpenChange={setPdfCreateOpen}
        onCreated={handlePdfCreated}
      />
    </Screen>
  );
}

function EmptyState({
  icon: Icon,
  message,
  onCreate,
}: {
  icon: typeof BookOpen;
  message: string;
  onCreate: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <Icon className="size-12 text-[var(--text-muted)]" />
      <p className="text-[var(--text-muted)]">{message}</p>
      <Button onClick={onCreate}>
        <Plus className="size-4 mr-1" /> 最初の資料を作成
      </Button>
    </div>
  );
}

/**
 * 資料カード共通コンポーネント。
 *
 * カード全体は role="button" の div として扱い、内側に本物の <button> (削除) を
 * 配置できるようにする。ネストしたインタラクティブ要素は個別に stopPropagation
 * で開閉できる (delete ボタンは handleDelete 側で stopPropagation)。
 *
 * キーボードでのアクセシビリティ:
 *   - Enter / Space でカードを開く
 *   - Delete ボタンは Tab で個別にフォーカス可能
 */
function DocumentCard({
  children,
  onOpen,
  onDelete,
  deleting,
}: {
  children: React.ReactNode;
  onOpen: () => void;
  onDelete: (e: React.MouseEvent) => void;
  deleting: boolean;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative text-left p-4 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
    >
      {children}
      {/* 削除ボタン: 通常時は控えめ、hover / focus 時に濃く。カードクリックとは stopPropagation で分離 */}
      <button
        type="button"
        onClick={onDelete}
        disabled={deleting}
        aria-label="この資料を削除"
        title="削除"
        className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-[var(--text-muted)] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-600 hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <Trash2 className="size-3" />
        {deleting ? '削除中…' : '削除'}
      </button>
    </div>
  );
}
